import { Injectable } from '@angular/core';
import { Building, SessionService } from '@edl-development/edl-common';
import { firstValueFrom, forkJoin } from 'rxjs';
import { BuildingService } from '../building.service';
import { PaetepPortalService } from '../paetep-portal.service';
import { PermissionsService } from '../permissions/permissions.service';
import { LocalStorageKeys } from '../../model/enum/local-storage-keys.enum';
import * as _ from 'lodash';
import { LocalStorageLocationKeys } from '../../model/enum/local-storage-location-keys.enum';
import { CustomSupervisionService } from '../custom-supervision/custom-supervision.service';

@Injectable({
    providedIn: 'root'
})
export class SessionHelpersService {

    constructor(
        private sessionService: SessionService,
        private buildingService: BuildingService,
        private paetepPortalService: PaetepPortalService,
        private permissionsService: PermissionsService,
        private customSupervisionService: CustomSupervisionService
    ) { }

    GetSessionValue(key: LocalStorageKeys): any {
        return this.sessionService.GetSessionValue(key);
    }

    SetSessionValue(key: LocalStorageKeys, value: any): void {
        this.sessionService.SetSessionValue(key, value);
    }

    getFromLocalStorageLocation(key: LocalStorageKeys, loc: LocalStorageLocationKeys): any {
        return this.sessionService.GetSessionValue(this.generateLocalStorageLocationKey(key, loc));
    }

    setFromLocalStorageLocation(key: LocalStorageKeys, loc: LocalStorageLocationKeys, value: any): void {
        this.sessionService.SetSessionValue(this.generateLocalStorageLocationKey(key, loc), value);
    }

    private generateLocalStorageLocationKey(key: LocalStorageKeys, loc: LocalStorageLocationKeys) {
        return `${key}_${loc}`;
    }

    getSchoolYears = (): string[] => <string[]>this.sessionService.GetSessionValue('school_years') ?? [];

    getDefaultSchoolYear(sessionKey?: string): string {
        const sYs: any = this.sessionService.GetSessionValue('school_years');
        const dsY: any = this.sessionService.GetSessionValue(sessionKey || 'user_default_schoolYear');

        return dsY != undefined ? dsY : sYs[0];
    }

    getDefaultBuildingID(buildings: Building[] = [], canViewAllBuildings:boolean = false, sessionkey?:string): number | undefined {
        const defaultBuildingId = sessionkey ? this.sessionService.GetSessionValue(sessionkey) as number : this.sessionService.GetSessionValue('user-default-building') as number;
        if (canViewAllBuildings && defaultBuildingId &&  defaultBuildingId.toString() == '-1') return  defaultBuildingId;
        else return buildings.find(b => b.buildingID == defaultBuildingId)?.buildingID ?? buildings[0].buildingID;
    }

    getBuildings = () => {
        return <Building[]>(this.sessionService.GetSessionValue(LocalStorageKeys.BUILDINGS) ?? []);
    }

    getMyTargetPeerIds = (): Set<string> => {
        return new Set<string>(<string[]>(this.sessionService.GetSessionValue(LocalStorageKeys.MY_TARGET_PEERS) ?? []))
    }

    reloadMyTargetPeerIds = async () => {
        const myTargetPeerIds = await firstValueFrom(this.customSupervisionService.getAllMyEmployeeIds());
        this.setMyTargetPeerIds(myTargetPeerIds);
    }

    private setMyTargetPeerIds = (peerIds?: string[] | null) => {
        this.sessionService.SetSessionValue(LocalStorageKeys.MY_TARGET_PEERS, [... new Set<string>(peerIds ?? [])]);
    }

    loadSessionData = () => {
        return firstValueFrom(
            forkJoin({
                buildings: this.buildingService.getPortalBuildings(),
                portalModuleSettings: this.paetepPortalService.getPortalModuleSettings(),
                permissions: this.permissionsService.getMyPermissions(),
                rolesICanManage: this.permissionsService.getRolesICanManage(),
                rolesICanManagePerBuilding: this.permissionsService.getRolesICanManagePerBuilding(),
                myTargetPeerIds: this.customSupervisionService.getAllMyEmployeeIds()
            })
        ).then(({ buildings, portalModuleSettings, permissions, rolesICanManage, rolesICanManagePerBuilding, myTargetPeerIds }) => {
            let sortedBuildings = _.sortBy(buildings, 'name');
            const permissionsMap = this.generatePermissionsMap(permissions);
            
            this.setMyTargetPeerIds(myTargetPeerIds);
            this.sessionService.SetSessionValue(LocalStorageKeys.BUILDINGS, sortedBuildings);
            this.sessionService.SetSessionValue('edl_portal_module_settings', portalModuleSettings );
            this.sessionService.SetSessionValue(LocalStorageKeys.PERMISSIONS, permissionsMap);
            this.sessionService.SetSessionValue(LocalStorageKeys.ROLES_I_CAN_MANAGE, rolesICanManage);
            this.sessionService.SetSessionValue(LocalStorageKeys.ROLES_I_CAN_MANAGE_PER_BUILDING, rolesICanManagePerBuilding);
            this.sessionService.SetSessionValue(LocalStorageKeys.HAS_LOADED_DATA, true);
        });
    }

    private generatePermissionsMap(permissions?: string[] | null) {
        return permissions?.reduce((currentMap: {[key: string]: boolean}, permission: string) => (currentMap[permission] = true, currentMap), {}) ?? {}
    }
}
